import { useEffect, useRef, useState } from "react";
import { Settings as SettingsIcon, Save, Download, Upload, Layers, RotateCcw, Plus, Trash2, Cpu } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { stationLabel } from "@/utils/processStation";
import OverheadsTable from "@/components/OverheadsTable";
import MySignaturePanel from "@/components/MySignaturePanel";
import { useAuth } from "@/context/AuthContext";

const EDITABLE_SETTINGS = [
  { key: "company_name",                 label: "Company Name",               hint: "" },
  { key: "company_address",              label: "Company Address",            hint: "Appears in Excel header" },
  { key: "margin_red_flag_threshold_pct",label: "Margin Red-Flag Threshold %", hint: "NPDs below this trigger a red alert" },
  { key: "tool_maintenance_default_pct", label: "Tool Maintenance % (default)", hint: "Decimal; 0.10 = 10% of Process Cost" },
  { key: "scrap_recovery_default_factor",label: "Scrap Recovery Factor (default)", hint: "Decimal; 0.9 means 90% recovery" },
  { key: "quote_validity_days",          label: "Quote Validity (days)",      hint: "Printed on Excel" },
  { key: "gst_mandatory_line",           label: "GST Mandatory Line",         hint: "Always appended to T&C on Excel" },
  // Machine tonnage list moved to Masters → Machines (its own page).
];

const DEFAULT_MASTER_OVERHEADS = [
  { label: "Factory Overhead", value: 0.08, basis: "sub_total", is_default: true, calculated: 0 },
  { label: "Admin Overhead",   value: 0.04, basis: "sub_total", is_default: true, calculated: 0 },
  { label: "Profit Margin",    value: 0.10, basis: "sub_total", is_default: true, calculated: 0 },
];

// Round 18 — Default Processes seed values (mirrors backend services/seed.py DEFAULT_PROCESSES)
const SEED_DEFAULT_PROCESSES = [
  { name: "Shearing",         cost_mode: "per_kg",    rate: 3.5, cost: 0.0 },
  { name: "De-burring",       cost_mode: "per_piece", rate: 0.0, cost: 0.0 },
  { name: "Cleaning",         cost_mode: "per_piece", rate: 0.0, cost: 0.0 },
  { name: "Final Inspection", cost_mode: "per_piece", rate: 0.0, cost: 0.0 },
];

function SettingsAuditPanel() {
  const [entries, setEntries] = useState([]);
  const [filterKey, setFilterKey] = useState("default_processes");
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/audit", { params: { key: filterKey || undefined } });
      setEntries(r.data.entries || []);
    } catch (e) {
      // silently ignore — user may not have view permission
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterKey]);
  const truncate = (v) => {
    const s = String(v ?? "");
    return s.length > 80 ? s.slice(0, 77) + "…" : s;
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 mt-6" data-testid="settings-audit-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold text-slate-900">Settings audit trail</h3>
          <p className="text-xs text-slate-500">Last 50 changes. Default filter: default_processes.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filterKey} onChange={(e) => setFilterKey(e.target.value)} className="px-2 py-1 border border-slate-300 rounded text-xs bg-white" data-testid="audit-key-filter">
            <option value="">All keys</option>
            <option value="default_processes">default_processes</option>
            <option value="default_overheads">default_overheads</option>
            <option value="company_name">company_name</option>
            <option value="company_address">company_address</option>
            <option value="margin_red_flag_threshold_pct">margin_red_flag_threshold_pct</option>
            <option value="gst_mandatory_line">gst_mandatory_line</option>
          </select>
          <button onClick={load} className="px-2 py-1 text-xs border border-slate-300 hover:bg-slate-50 rounded">Refresh</button>
        </div>
      </div>
      {loading ? (
        <div className="text-xs text-slate-400 italic py-3">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="text-xs text-slate-400 italic py-3">No changes recorded yet for this key.</div>
      ) : (
        <div className="overflow-x-auto">
        <table className="w-full min-w-[600px] text-xs">
          <thead className="text-slate-500 border-b border-slate-200">
            <tr>
              <th className="text-left py-2 px-2 w-32">When</th>
              <th className="text-left py-2 px-2 w-32">Who</th>
              <th className="text-left py-2 px-2 w-32">Key</th>
              <th className="text-left py-2 px-2">Old → New</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {entries.map((e) => (
              <tr key={e.log_id}>
                <td className="py-2 px-2 text-slate-500 font-mono text-[11px]">{new Date(e.changed_at).toLocaleString("en-IN")}</td>
                <td className="py-2 px-2 text-slate-700">{e.changed_by_name || "—"}</td>
                <td className="py-2 px-2 font-mono text-[11px] text-slate-600">{e.key}</td>
                <td className="py-2 px-2">
                  <div className="text-slate-400 font-mono text-[11px] line-through">{truncate(e.old_value)}</div>
                  <div className="text-slate-900 font-mono text-[11px]">{truncate(e.new_value)}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

// ---- Auto-backup config + server-stored backups (admin only) ----
const fmtBytes = (b) => {
  b = Number(b) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};
const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};
// Split a minute count into the largest whole unit for display.
const splitInterval = (mins) => {
  mins = Number(mins) || 0;
  if (mins > 0 && mins % 1440 === 0) return { value: mins / 1440, unit: "days" };
  if (mins > 0 && mins % 60 === 0) return { value: mins / 60, unit: "hours" };
  return { value: mins, unit: "minutes" };
};
const UNIT_TO_MIN = { minutes: 1, hours: 60, days: 1440 };

function AutoBackupPanel() {
  const [data, setData] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [intervalValue, setIntervalValue] = useState(1);
  const [intervalUnit, setIntervalUnit] = useState("days");
  const [retention, setRetention] = useState(5);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const applyConfig = (cfg) => {
    setEnabled(!!cfg.enabled);
    const { value, unit } = splitInterval(cfg.interval_minutes);
    setIntervalValue(value);
    setIntervalUnit(unit);
    setRetention(cfg.retention);
  };

  const load = async () => {
    try {
      const r = await api.get("/backup/auto");
      setData(r.data);
      applyConfig(r.data.config || {});
    } catch (e) { /* admin-only; ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      const interval_minutes = Math.round((Number(intervalValue) || 0) * UNIT_TO_MIN[intervalUnit]);
      const r = await api.put("/backup/auto", { enabled, interval_minutes, retention: Number(retention) || 1 });
      setData((d) => ({ ...(d || {}), ...r.data }));
      applyConfig(r.data.config || {});
      toast.success("Auto-backup settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/backups/run");
      setData((d) => ({ ...(d || {}), backups: r.data.backups }));
      toast.success("Backup created");
    } catch (e) { toast.error(e?.response?.data?.detail || "Backup failed"); }
    finally { setRunning(false); }
  };

  const download = async (b) => {
    try {
      const r = await api.get(`/backups/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = (r.headers?.["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || `${b.filename || "backup"}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const del = async (b) => {
    if (!window.confirm(`Delete backup from ${fmtTime(b.created_at)}? This cannot be undone.`)) return;
    try {
      const r = await api.delete(`/backups/${b.id}`);
      setData((d) => ({ ...(d || {}), backups: r.data.backups }));
      toast.success("Backup deleted");
    } catch (e) { toast.error("Delete failed"); }
  };

  const restore = async (b) => {
    const mode = window.prompt(`Restore from backup dated ${fmtTime(b.created_at)}?\nType 'merge' (keep existing, upsert) or 'replace' (wipe and insert)`, "merge");
    if (!mode || !["merge", "replace"].includes(mode)) return;
    try {
      const r = await api.post(`/backups/${b.id}/restore`, { mode });
      toast.success("Restore complete");
      console.log("Restore summary:", r.data);
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  };

  const state = data?.state || {};
  const backups = data?.backups || [];
  const statusColor = state.last_status === "ok" ? "text-emerald-600"
    : state.last_status === "error" ? "text-red-600" : "text-slate-500";

  return (
    <div className="mt-5 border-t border-slate-200 pt-5" data-testid="auto-backup-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="font-semibold text-slate-900 text-sm">Automatic backups</h4>
          <p className="text-xs text-slate-500">Scheduled server-side snapshots, keeping the most recent {retention} on a rolling basis.</p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="auto-backup-enabled">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4 accent-orange-600" />
          <span className="text-sm font-medium text-slate-700">{enabled ? "Enabled" : "Disabled"}</span>
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Run every</label>
          <div className="flex gap-2">
            <input
              type="number" min="1" value={intervalValue}
              onChange={(e) => setIntervalValue(e.target.value)}
              className="w-20 px-2 py-1.5 border border-slate-300 rounded text-sm"
              data-testid="auto-backup-interval-value"
            />
            <select
              value={intervalUnit} onChange={(e) => setIntervalUnit(e.target.value)}
              className="flex-1 px-2 py-1.5 border border-slate-300 rounded text-sm bg-white"
              data-testid="auto-backup-interval-unit"
            >
              <option value="minutes">Minutes</option>
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Keep last (backups)</label>
          <input
            type="number" min="1" max="50" value={retention}
            onChange={(e) => setRetention(e.target.value)}
            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
            data-testid="auto-backup-retention"
          />
        </div>
        <div className="flex items-end gap-2">
          <button onClick={save} disabled={saving} className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white text-sm rounded-md flex items-center gap-1.5" data-testid="auto-backup-save">
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
          <button onClick={runNow} disabled={running} className="px-3 py-1.5 border border-slate-300 hover:bg-slate-50 disabled:opacity-60 text-sm rounded-md flex items-center gap-1.5" data-testid="auto-backup-run-now">
            <Download size={14} /> {running ? "Working…" : "Back up now"}
          </button>
        </div>
      </div>

      <div className="text-xs text-slate-500 mb-4 flex flex-wrap gap-x-4 gap-y-1">
        <span>Last run: <span className="font-medium text-slate-700">{fmtTime(state.last_run_at)}</span></span>
        {state.last_status && <span>Status: <span className={`font-medium ${statusColor}`}>{state.last_status}</span></span>}
        {enabled && state.next_run_at && <span>Next run (approx): <span className="font-medium text-slate-700">{fmtTime(state.next_run_at)}</span></span>}
        {state.last_error && <span className="text-red-600">Error: {state.last_error}</span>}
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm" data-testid="stored-backups-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left p-2.5">Created</th>
                <th className="text-left p-2.5">Type</th>
                <th className="text-right p-2.5">Records</th>
                <th className="text-right p-2.5">Size</th>
                <th className="text-right p-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {backups.length === 0 ? (
                <tr><td colSpan={5} className="p-4 text-center text-slate-400 text-xs">No stored backups yet.</td></tr>
              ) : backups.map((b) => (
                <tr key={b.id} data-testid={`backup-row-${b.id}`}>
                  <td className="p-2.5 text-slate-700">{fmtTime(b.created_at)}</td>
                  <td className="p-2.5">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${b.kind === "auto" ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-600"}`}>
                      {b.kind === "auto" ? "Auto" : "Manual"}
                    </span>
                  </td>
                  <td className="p-2.5 text-right font-mono text-slate-600">{b.total_docs ?? "—"}</td>
                  <td className="p-2.5 text-right font-mono text-slate-600">{fmtBytes(b.size_bytes)}</td>
                  <td className="p-2.5 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-2">
                      <button onClick={() => download(b)} className="text-slate-400 hover:text-sky-600" title="Download" data-testid={`backup-download-${b.id}`}><Download size={15} /></button>
                      <button onClick={() => restore(b)} className="text-slate-400 hover:text-orange-600" title="Restore this backup" data-testid={`backup-restore-${b.id}`}><RotateCcw size={15} /></button>
                      <button onClick={() => del(b)} className="text-slate-400 hover:text-red-600" title="Delete" data-testid={`backup-delete-${b.id}`}><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "npd_manager";
  const [settings, setSettings] = useState({});
  const [masterOverheads, setMasterOverheads] = useState([]);
  const [defaultProcesses, setDefaultProcesses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savingProcesses, setSavingProcesses] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      // Round 20 — managers cannot hit admin-only GET /settings; use public endpoint which exposes default_processes.
      if (isManager) {
        const r = await api.get("/settings/public");
        const map = r.data || {};
        setSettings(map);
        try {
          const procs = JSON.parse(map.default_processes || "[]");
          setDefaultProcesses(Array.isArray(procs) && procs.length > 0 ? procs : SEED_DEFAULT_PROCESSES);
        } catch {
          setDefaultProcesses(SEED_DEFAULT_PROCESSES);
        }
        return;
      }
      const r = await api.get("/settings");
      const map = {};
      r.data.forEach((row) => { map[row.key] = row.value; });
      setSettings(map);
      // Master overheads
      try {
        const oh = JSON.parse(map.default_overheads || "[]");
        setMasterOverheads(Array.isArray(oh) && oh.length > 0 ? oh : DEFAULT_MASTER_OVERHEADS);
      } catch {
        setMasterOverheads(DEFAULT_MASTER_OVERHEADS);
      }
      // Round 18 — Default processes
      try {
        const procs = JSON.parse(map.default_processes || "[]");
        setDefaultProcesses(Array.isArray(procs) && procs.length > 0 ? procs : SEED_DEFAULT_PROCESSES);
      } catch {
        setDefaultProcesses(SEED_DEFAULT_PROCESSES);
      }
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isManager]);

  const update = (k, v) => setSettings((p) => ({ ...p, [k]: v }));

  // Combined save for the merged "Master Defaults" card — persists Overheads (admin only)
  // and the Processes master together in one click.
  const saveMasterDefaults = async () => {
    setSavingProcesses(true);
    try {
      if (isAdmin) {
        const oh = (masterOverheads || []).map((r) => ({
          label: r.label || "",
          value: Number(r.value) || 0,
          basis: r.basis || "sub_total",
          is_default: true,
        }));
        await api.put(`/settings/default_overheads`, { value: JSON.stringify(oh) });
      }
      const cleaned = (defaultProcesses || []).map((p) => ({
        name: p.name || "",
        cost_mode: p.cost_mode || "per_piece",
        rate: Number(p.rate) || 0,
        cost: Number(p.cost) || 0,
        cycle_time_sec: Number(p.cycle_time_sec) || 0,
      }));
      await api.put(`/settings/default_processes`, { value: JSON.stringify(cleaned) });
      toast.success("Master defaults saved — applies to new costings going forward");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingProcesses(false); }
  };

  const resetProcessesToSeed = () => {
    if (!window.confirm("Reset default processes to the seed values? Your current master will be replaced.")) return;
    setDefaultProcesses(SEED_DEFAULT_PROCESSES.map((p) => ({ ...p })));
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      for (const { key } of EDITABLE_SETTINGS) {
        if (settings[key] !== undefined) {
          await api.put(`/settings/${key}`, { value: String(settings[key]) });
        }
      }
      // Overheads + processes are saved together from the "Master Defaults" card's Save Master
      // button (single source), so they're intentionally not re-saved here.
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const backup = async () => {
    try {
      const r = await api.get("/backup");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `aampl-npd-backup-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) { toast.error("Backup failed"); }
  };

  const restore = async (ev) => {
    const f = ev.target.files?.[0]; if (!f) return;
    const mode = window.prompt("Restore mode: type 'merge' (keep existing, upsert) or 'replace' (wipe and insert)", "merge");
    if (!mode || !["merge", "replace"].includes(mode)) return;
    try {
      const text = await f.text();
      const payload = JSON.parse(text);
      payload.mode = mode;
      const r = await api.post("/restore", payload);
      toast.success("Restore complete");
      console.log("Restore summary:", r.data);
      setTimeout(() => window.location.reload(), 1000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
    if (fileRef.current) fileRef.current.value = "";
  };

  if (loading) return <div className="p-10 text-center text-slate-400">Loading…</div>;

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="settings-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><SettingsIcon size={22} className="text-orange-600" /> Settings</h1>
          <p className="text-sm text-slate-500 mt-0.5">App configuration and backup & restore. Manage users on the Users page.</p>
        </div>
        {isAdmin && (
          <button onClick={saveAll} disabled={saving} className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white text-sm font-semibold rounded-md flex items-center gap-1.5" data-testid="settings-save">
            <Save size={14} /> {saving ? "Saving…" : "Save Settings"}
          </button>
        )}
      </div>

      {isManager && (
        <div className="mb-4 bg-sky-50 border border-sky-200 rounded-md p-3 text-sm text-sky-800" data-testid="manager-settings-banner">
          You can edit the Default Processes Master below. Other app settings are admin-only.
        </div>
      )}

      {/* R30 — Self-service signature panel, visible to every signed-in user */}
      <MySignaturePanel />

      {isAdmin && (
      <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6">
        <h3 className="font-semibold text-slate-900 mb-4">App Configuration</h3>
        <div className="space-y-3">
          {EDITABLE_SETTINGS.map((s) => (
            <div key={s.key}>
              <label className="block text-xs font-medium text-slate-600">{s.label}</label>
              {s.hint && <div className="text-xs text-slate-400 mb-1">{s.hint}</div>}
              {s.key === "company_address" || s.key === "gst_mandatory_line" ? (
                <textarea value={settings[s.key] || ""} onChange={(e) => update(s.key, e.target.value)} rows={2} className="input" />
              ) : (
                <input value={settings[s.key] || ""} onChange={(e) => update(s.key, e.target.value)} className="input" data-testid={`setting-${s.key}`} />
              )}
            </div>
          ))}
        </div>
      </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6" data-testid="master-defaults-section">
        <div className="flex items-start justify-between mb-4 gap-3">
          <div>
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Layers size={16} className="text-orange-600" /> Master Defaults — {isAdmin ? "Overheads & Processes" : "Processes"}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Templates applied to every new NPD costing, assembly, and project part. Edit below and click "Save Master". Existing costings are not affected.
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={resetProcessesToSeed}
              className="px-3 py-1.5 border border-slate-300 hover:bg-slate-50 text-xs rounded-md flex items-center gap-1.5"
              data-testid="processes-reset-seed"
            >
              <RotateCcw size={12} /> Reset processes
            </button>
            <button
              onClick={saveMasterDefaults}
              disabled={savingProcesses}
              className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white text-xs font-semibold rounded-md flex items-center gap-1.5"
              data-testid="master-defaults-save"
            >
              <Save size={12} /> {savingProcesses ? "Saving…" : "Save Master"}
            </button>
          </div>
        </div>

        {isAdmin && (
          <div className="mb-6" data-testid="master-overheads-section">
            <h4 className="text-sm font-semibold text-slate-800 mb-1 flex items-center gap-1.5">
              <Layers size={14} className="text-orange-600" /> Overheads &amp; Margin
            </h4>
            <p className="text-xs text-slate-500 mb-3">
              Applied to every new NPD costing and assembly. Edit labels, percentages, and basis.
            </p>
            <OverheadsTable
              overheads={masterOverheads}
              onChange={setMasterOverheads}
              masterMode
            />
          </div>
        )}

        <div data-testid="default-processes-section">
          {isAdmin && <div className="border-t border-slate-200 pt-4" />}
          <h4 className="text-sm font-semibold text-slate-800 mb-1 flex items-center gap-1.5">
            <Cpu size={14} className="text-orange-600" /> Processes
          </h4>
          <p className="text-xs text-slate-500 mb-3">
            Pre-applied on every new NPD. Engineers can change rates and add their own stamping processes per-costing. For "per kg" mode, the per-piece cost is computed as <code>rate × gross_weight</code>.
          </p>

          <div className="border border-slate-200 rounded-md overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[600px] text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left p-2 w-8">#</th>
                <th className="text-left p-2">Process Name</th>
                <th className="text-left p-2 w-32">Mode</th>
                <th className="text-right p-2 w-32">Default Rate</th>
                <th className="text-right p-2 w-28" title="Auto-fills the process cycle time on every new part">Cycle Time (s)</th>
                <th className="text-center p-2 w-28" title="Auto-selected tonnage/station on the costing sheet">Auto Tonnage</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {defaultProcesses.map((p, idx) => (
                <tr key={idx} className="hover:bg-slate-50">
                  <td className="p-2 text-slate-400 font-mono text-xs">{idx + 1}</td>
                  <td className="p-2">
                    <input
                      value={p.name || ""}
                      onChange={(e) => {
                        const next = [...defaultProcesses];
                        next[idx] = { ...next[idx], name: e.target.value };
                        setDefaultProcesses(next);
                      }}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                      data-testid={`master-process-name-${idx}`}
                    />
                  </td>
                  <td className="p-2">
                    <select
                      value={p.cost_mode || "per_piece"}
                      onChange={(e) => {
                        const next = [...defaultProcesses];
                        next[idx] = { ...next[idx], cost_mode: e.target.value, rate: 0, cost: 0 };
                        setDefaultProcesses(next);
                      }}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm bg-white"
                      data-testid={`master-process-mode-${idx}`}
                    >
                      <option value="per_piece">₹ per piece</option>
                      <option value="per_kg">₹ per kg</option>
                    </select>
                  </td>
                  <td className="p-2">
                    <input
                      type="number"
                      step="0.01"
                      value={(p.cost_mode === "per_kg") ? (p.rate ?? 0) : (p.cost ?? p.rate ?? 0)}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value) || 0;
                        const next = [...defaultProcesses];
                        if ((next[idx].cost_mode || "per_piece") === "per_kg") {
                          next[idx] = { ...next[idx], rate: v };
                        } else {
                          next[idx] = { ...next[idx], cost: v, rate: v };
                        }
                        setDefaultProcesses(next);
                      }}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm font-mono text-right"
                      data-testid={`master-process-rate-${idx}`}
                    />
                  </td>
                  <td className="p-2">
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={p.cycle_time_sec ?? 0}
                      onChange={(e) => {
                        const next = [...defaultProcesses];
                        next[idx] = { ...next[idx], cycle_time_sec: parseFloat(e.target.value) || 0 };
                        setDefaultProcesses(next);
                      }}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm font-mono text-right"
                      data-testid={`master-process-cycle-${idx}`}
                    />
                  </td>
                  <td className="p-2 text-center">
                    {(() => {
                      const s = stationLabel(p.name);
                      if (s.kind === "station") return <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-xs font-semibold" title="Auto-filled on the costing sheet">{s.label}</span>;
                      if (s.kind === "no-machine") return <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-xs font-medium" title="No tonnage — excluded from Machine Requirement">{s.label}</span>;
                      return <span className="text-slate-300 text-xs" title="Press step — engineer picks the tonnage">—</span>;
                    })()}
                  </td>
                  <td className="p-2">
                    <button
                      type="button"
                      onClick={() => setDefaultProcesses(defaultProcesses.filter((_, i) => i !== idx))}
                      className="text-slate-300 hover:text-red-500"
                      title="Remove"
                      data-testid={`master-process-remove-${idx}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <div className="border-t border-slate-200 p-2 bg-slate-50">
            <button
              type="button"
              onClick={() => setDefaultProcesses([
                ...defaultProcesses,
                { name: "Custom Process", cost_mode: "per_piece", rate: 0, cost: 0, cycle_time_sec: 0 },
              ])}
              className="text-sm text-orange-600 hover:text-orange-700 flex items-center gap-1"
              data-testid="master-process-add"
            >
              <Plus size={14} /> Add default process
            </button>
          </div>
        </div>
        </div>
      </div>

      {isAdmin && (
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h3 className="font-semibold text-slate-900 mb-2">Backup & Restore</h3>
        <p className="text-xs text-slate-500 mb-4">JSON export of customers, materials, NPDs, rounds, settings, T&amp;C, the full APQP data (projects, tasks, matrix &amp; masters) and ballooning. Drawings/files in object storage are not in this file — file references are preserved.</p>
        <div className="flex flex-wrap gap-3">
          <button onClick={backup} className="px-4 py-2 border border-slate-300 hover:bg-slate-50 text-sm rounded-md flex items-center gap-1.5" data-testid="backup-btn">
            <Download size={14} /> Download Backup
          </button>
          <label className="px-4 py-2 border border-slate-300 hover:bg-slate-50 text-sm rounded-md flex items-center gap-1.5 cursor-pointer">
            <Upload size={14} /> Restore from JSON
            <input ref={fileRef} type="file" accept=".json" className="hidden" onChange={restore} />
          </label>
        </div>
        <AutoBackupPanel />
      </div>
      )}
      <SettingsAuditPanel />
    </div>
  );
}
